import React, { useRef, useState, useEffect } from 'react';

import jsPDF from 'jspdf';
import apiClient from '../../apiClient';
import html2canvas from 'html2canvas';
import { numberToWordsIndian } from '../../utlis/numberToWordsIndian';
import { formatCurrency } from '../../utlis/formatCurrency';
import {
  invoiceDate,
  placeOfSupply,
  supplier,
  recipient,
  bank,
  getDefaultProduct,
  getCurrentMonthYearSuffix,
  packageOptions
} from '../../constants/invoiceConstants';
import './invoice.css';

// Import components
import InvoiceHeader from '../../Component/invoice/InvoiceHeader';
import InvoiceMeta from '../../Component/invoice/InvoiceMeta';
import RecipientSection from '../../Component/invoice/RecipientSection';
import InvoiceTable from '../../Component/invoice/InvoiceTable';
import SummaryBankRow from '../../Component/invoice/SummaryBankRow';
import TermsConditions from '../../Component/invoice/TermsConditions';
import InvoiceFooter from '../../Component/invoice/InvoiceFooter';
import InvoiceControls from '../../Component/invoice/invoiceControl';
import './InvoiceCreate.css';

const InvoiceCreate = ({ invoice, isEditable: propIsEditable, onBack }) => {  
  const invoiceRef = useRef();
  const [invoiceType, setInvoiceType] = useState(invoice?.invoice_type || "TAX INVOICE");
  const [isEditable, setIsEditable] = useState(propIsEditable || false);
  const [paidAmount, setPaidAmount] = useState(invoice?.paidAmount || 0);
  const [totals, setTotals] = useState({
    taxable: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    total: 0
  });
const [termsConditions, setTermsConditions] = useState(
  invoice?.terms_conditions || '1. All packages are non-customizable and apply uniformly to all clients.\n2. Once payment is processed, it is non-refundable.\n3. Vidyut Cloud exclusively provides leads and does not engage in transactions, shipping, or any other activities beyond lead generation.\n4. The volume of leads is subject to market conditions and may fluctuate accordingly.\n5. Vendor sourcing and digital marketing services are included.\n6. Digital marketing includes 1 months of advertisement on VidyutCloud\'s homepage (banner service) & other social media platforms.'
);

  // useEffect(() => {
  //   const firstProduct = invoiceData.products[0];
  //   if (firstProduct?.packageType === 'SELLER SERVICES PACKAGE' && firstProduct.duration) {
  //     const years = parseInt(firstProduct.duration);
  //     if (!isNaN(years)) {
  //       const months = years;
  //       const monthText = months > 1 ? 'months' : 'month';
  //       const point6 = `6. Digital marketing includes ${months} ${monthText} of advertisement on VidyutCloud's homepage (banner service) & other social media platforms.`;
  //       const otherTerms = `1. All packages are non-customizable and apply uniformly to all clients.\n2. Once payment is processed, it is non-refundable.\n3. Vidyut Cloud exclusively provides leads and does not engage in transactions, shipping, or any other activities beyond lead generation.\n4. The volume of leads is subject to market conditions and may fluctuate accordingly.\n5. Vendor sourcing and digital marketing services are included.\n`;

  //     }
  //   }
  // }, [invoiceData.products]);

  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(null);


  // Function to generate invoice number prefix based on invoice type
  const getInvoicePrefix = (type) => {
    if (type === "TAX INVOICE") {
      return "MH/TI";
    } else if (type === "PROFORMA INVOICE") {
      return "MH/PI";
    } else {
      return "MH/XX";
    }
  };

  // Function to extract or generate the sequential number
  const getSequentialNumber = (existingInvoiceNo) => {
    if (existingInvoiceNo) {
      const parts = existingInvoiceNo.split('/');
      if (parts.length >= 3) {
        return parts[2]; // Return the numeric part (006, etc.)
      }
    }
    return 'ABC'; // Default starting number
  };

  // Initialize invoiceData with proper invoice number format
  const [invoiceData, setInvoiceData] = useState(() => {
    const prefix = getInvoicePrefix(invoice?.invoice_type || "TAX INVOICE");
    
    let invNumber = '';
    let fullInvoiceNo = '';
    
    if (invoice?.invoice_no) {
      // If we have an existing invoice, use its number
      invNumber = getSequentialNumber(invoice.invoice_no);
      fullInvoiceNo = invoice.invoice_no;
    } else {
      // Generate new invoice number
      invNumber = ''; // Will be updated when saving
      const monthYearSuffix = getCurrentMonthYearSuffix();
      fullInvoiceNo = `${prefix}/${invNumber}/${monthYearSuffix}`;
    }
    
    return {
      inv_number: invNumber,
      invoiceNo: fullInvoiceNo,
      invoiceDate: invoice?.invoice_date || invoiceDate,
      placeOfSupply: invoice?.placeOfSupply || placeOfSupply,
      supplier: invoice?.supplier || supplier,
      recipient: invoice?.recipient || recipient,
      bank: invoice?.bank || bank,
      products: invoice?.products || [getDefaultProduct()]
    };
  });

  // Update invoiceNo when invoiceType or inv_number changes
  useEffect(() => {
    setInvoiceData(prev => {
      const parts = prev.invoiceNo.split('/');
      const invNumber = prev.inv_number || getSequentialNumber(prev.invoiceNo);
      const monthYearSuffix = parts.length > 3 ? parts.slice(3).join('/') : getCurrentMonthYearSuffix();
      const newPrefix = getInvoicePrefix(invoiceType);
      
      return {
        ...prev,
        invoiceNo: `${newPrefix}/${invNumber}/${monthYearSuffix}`
      };
    });
  }, [invoiceType, invoiceData.inv_number]);

  // Fetch last invoice number when invoiceType changes, only if new invoice (no id)
  useEffect(() => {
    if (invoice && invoice.id) {
      // Editing existing invoice, do not fetch last invoice number
      return;
    }

    const fetchLastInvoiceNumber = async () => {
      try {
        let response;
        if (invoiceType.toUpperCase() === "TAX INVOICE") {
          response = await apiClient.get('/api/invoices/last-tax-invoice-number');
        } else if (invoiceType.toUpperCase() === "PROFORMA INVOICE") {
          response = await apiClient.get('/api/invoices/last-proforma-invoice-number');
        } else {
          setLastInvoiceNumber(null);
          return;
        }

        const lastInvoiceNo = response.data?.lastInvoiceNo || null;
        setLastInvoiceNumber(lastInvoiceNo);

        if (lastInvoiceNo) {
          const parts = lastInvoiceNo.split('/');
          const lastNumber = parseInt(parts[2]) || 0;
          const nextNumber = String(lastNumber + 1).padStart(3, '0');
          const prefix = getInvoicePrefix(invoiceType);
          const monthYearSuffix = parts.length > 3 ? parts.slice(3).join('/') : getCurrentMonthYearSuffix();

          setInvoiceData(prev => ({
            ...prev,
            inv_number: nextNumber,
            invoiceNo: `${prefix}/${nextNumber}/${monthYearSuffix}`
          }));
        } else {
          // No invoices for current month, start with 001
          const nextNumber = '001';
          const prefix = getInvoicePrefix(invoiceType);
          const monthYearSuffix = getCurrentMonthYearSuffix();

          setInvoiceData(prev => ({
            ...prev,
            inv_number: nextNumber,
            invoiceNo: `${prefix}/${nextNumber}/${monthYearSuffix}`
          }));
        }
      } catch (error) {
        console.error('Error fetching last invoice number:', error);
        setLastInvoiceNumber(null);
      }
    };

    fetchLastInvoiceNumber();
  }, [invoiceType, invoice]);


  // Handler for inv_number changes
  const handleInvNumberChange = (e) => {
    const newNumber = e.target.value;
    // Validate it's a 3-5 digit number
    if (/^\d{0,5}$/.test(newNumber)) {
      setInvoiceData(prev => ({
        ...prev,
        inv_number: newNumber
      }));
    }
  };


  const saveInvoiceToDB = async () => {
    try {
      // If this is a new invoice, get the next sequential number
      if (!invoice?.id) {
        try {
          const response = await apiClient.get('/api/invoices/last-tax-invoice-number');
          const lastInvoiceNo = response.data?.lastInvoiceNo || `MH/PI/000/${getCurrentMonthYearSuffix()}`;
          const lastNumber = parseInt(lastInvoiceNo.split('/')[2]) || 0;
          const nextNumber = String(lastNumber + 1).padStart(3, '0');
          
          setInvoiceData(prev => {
            const parts = prev.invoiceNo.split('/');
            const monthYearSuffix = parts.length > 3 ? parts.slice(3).join('/') : getCurrentMonthYearSuffix();
            const prefix = getInvoicePrefix(invoiceType);
            
            return {
              ...prev,
              inv_number: nextNumber,
              invoiceNo: `${prefix}/${nextNumber}/${monthYearSuffix}`
            };
          });
          
          // Wait for state to update before proceeding
          await new Promise(resolve => setTimeout(resolve, 0));
        } catch (error) {
          console.error('Error getting last invoice number:', error);
          // Continue with default number if API fails
        }
      }

      const input = invoiceRef.current;
      const canvas = await html2canvas(input, {
        scale: 1.5,
        useCORS: true,
        letterRendering: true,
        width: input.scrollWidth,
        height: input.scrollHeight,
        windowWidth: input.scrollWidth,
        windowHeight: input.scrollHeight
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.85);

      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const finalWidth = imgWidth * ratio;
      const finalHeight = imgHeight * ratio;

      pdf.addImage(imgData, 'JPEG', 0, 0, finalWidth, finalHeight, undefined, 'FAST');

      const pdfBase64 = pdf.output('datauristring').split(',')[1];

      const now = new Date().toISOString().slice(0, 19).replace('T', ' ');

      let formattedInvoiceDate = '';
      if (invoiceData.invoiceDate.includes('T')) {
        formattedInvoiceDate = new Date(invoiceData.invoiceDate).toISOString().slice(0, 10);
      } else {
        const [day, month, year] = invoiceData.invoiceDate.split('-');
        formattedInvoiceDate = `${year}-${month}-${day}`;
      }

      const postData = {
        inv_number: invoiceData.inv_number,
        invoice_no: invoiceData.invoiceNo,
        invoice_type: invoiceType,
        invoice_date: formattedInvoiceDate,
        supply_place: invoiceData.placeOfSupply,
        name: invoiceData.recipient.name,
        address: invoiceData.recipient.address,
        state: invoiceData.recipient.state,
        invoice_data: JSON.stringify(invoiceData.products),
        package: invoiceData.products[0].packageType || '',
        duration: invoiceData.products[0].duration || '',
        hsn: invoiceData.products[0].hsn || '',
        qty: invoiceData.products[0].qty || 1,
        Taxable_amt: totals.taxable,
        created_at: now,
        updated_at: now,
        Total_amt: totals.total,
        CGST: totals.cgst,
        SGST: totals.sgst,
        IGST: totals.igst,
        Paid_Amt: paidAmount,
        GST_no: invoiceData.recipient.gstin,
        email: invoiceData.recipient.email,
        phone_no: invoiceData.recipient.contact,
        invoice_copy: pdfBase64,
        terms_conditions: termsConditions
      };

      let response;
      if (invoice && invoice.id) {
        response = await apiClient.put(`/api/invoices/${invoice.id}`, postData);
      } else {
        response = await apiClient.post('/api/invoices', postData);
      }

      if (response.status === 201 || response.status === 200) {
        alert('Invoice saved successfully' + (response.data.invoiceId ? ' with ID: ' + response.data.invoiceId : ''));
      } else {
        alert('Failed to save invoice');
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Error saving invoice');
    }
  };

  useEffect(() => {
    let taxable = 0, igst = 0, cgst = 0, sgst = 0;
    const isSameState = invoiceData.placeOfSupply.toLowerCase() === invoiceData.supplier.state.toLowerCase();

    const productsNeedUpdate = invoiceData.products.some(p => 
      p.cgst === undefined || p.sgst === undefined || p.igst === undefined
    );

    if (productsNeedUpdate) {
      const updatedProducts = invoiceData.products.map((p) => {
        const itemTaxable = p.qty * p.rate - p.discount;
        let itemCGST = 0, itemSGST = 0, itemIGST = 0;

        if (isSameState) {
          itemCGST = itemTaxable * 0.09;
          itemSGST = itemTaxable * 0.09;
        } else {
          itemIGST = itemTaxable * 0.18;
        }

        cgst += itemCGST;
        sgst += itemSGST;
        igst += itemIGST;
        taxable += itemTaxable;

        return {
          ...p,
          cgst: parseFloat(itemCGST.toFixed(2)),
          sgst: parseFloat(itemSGST.toFixed(2)),
          igst: parseFloat(itemIGST.toFixed(2))
        };
      });

      setInvoiceData(prev => ({
        ...prev,
        products: updatedProducts
      }));
    } else {
      invoiceData.products.forEach((p) => {
        const itemTaxable = p.qty * p.rate - p.discount;
        cgst += p.cgst || 0;
        sgst += p.sgst || 0;
        igst += p.igst || 0;
        taxable += itemTaxable;
      });
    }

    setTotals({
      taxable: parseFloat(taxable.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      igst: parseFloat(igst.toFixed(2)),
      total: parseFloat((taxable + cgst + sgst + igst).toFixed(2))
    });
  }, [invoiceData.placeOfSupply, invoiceData.supplier.state, invoiceData.products]);

  useEffect(() => {
    const firstProduct = invoiceData.products[0];
    if (firstProduct?.packageType === 'SELLER SERVICES PACKAGE' && firstProduct.duration) {
      const years = parseInt(firstProduct.duration);
      if (!isNaN(years)) {
        const months = years;
        const monthText = months > 1 ? 'months' : 'month';
        const point6 = `6. Digital marketing includes ${months} ${monthText} of advertisement on VidyutCloud's homepage (banner service) & other social media platforms.`;
        const otherTerms = `1. All packages are non-customizable and apply uniformly to all clients.\n2. Once payment is processed, it is non-refundable.\n3. Vidyut Cloud exclusively provides leads and does not engage in transactions, shipping, or any other activities beyond lead generation.\n4. The volume of leads is subject to market conditions and may fluctuate accordingly.\n5. Vendor sourcing and digital marketing services are included.\n`;
        setTermsConditions(otherTerms + point6);
      }
    }
  }, [invoiceData.products]);

  const amountInWords = numberToWordsIndian(Math.round(totals.total - paidAmount));

  const downloadPDF = () => {
    const input = invoiceRef.current;
    const invoiceTypeDiv = document.querySelector('.invoice-type-select');
    const lastInvoiceNumberElement = document.querySelector('.last-invoice-number-display');
    const invoiceNumberParagraph = document.querySelector('.invoice-meta p:first-child');

    if (invoiceTypeDiv) invoiceTypeDiv.style.display = 'none';
    if (lastInvoiceNumberElement) lastInvoiceNumberElement.style.display = 'none';
    if (invoiceNumberParagraph) invoiceNumberParagraph.style.display = 'none';

    input.style.paddingLeft = '20mm';
    input.style.paddingRight = '20mm';
    input.style.background = '#fff';

    const options = {
      scale: 2,
      useCORS: true,
      logging: false,
      letterRendering: true,
      windowWidth: input.scrollWidth,
      windowHeight: input.scrollHeight
    };

    html2canvas(input, options).then((canvas) => {
      const imgData = canvas.toDataURL('image/jpeg', 0.85);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const finalWidth = imgWidth * ratio;
      const finalHeight = imgHeight * ratio;

      const marginLeft = (pdfWidth - finalWidth) / 2;
      const marginTop = 10;

      pdf.addImage(imgData, 'JPEG', marginLeft, marginTop, finalWidth, finalHeight, undefined, 'FAST');

      const typeForFileName = invoiceType.toUpperCase().replace(/\s+/g, '_');
      pdf.save(`${typeForFileName}_${invoiceData.invoiceNo}.pdf`);

      input.style.paddingLeft = '';
      input.style.paddingRight = '';
      input.style.background = '';
      if (invoiceTypeDiv) invoiceTypeDiv.style.display = 'flex';
      if (lastInvoiceNumberElement) lastInvoiceNumberElement.style.display = 'block';
      if (invoiceNumberParagraph) invoiceNumberParagraph.style.display = 'block';
    });
  };

  const updatePackageDetails = (index, field, value) => {
    const updatedProducts = [...invoiceData.products];
    updatedProducts[index][field] = value;

    if (field === 'packageType' || field === 'duration') {
      const packageType = field === 'packageType' ? value : updatedProducts[index].packageType;
      const duration = field === 'duration' ? value : updatedProducts[index].duration;
      const rate = packageOptions[packageType]?.[duration] || 0;

      updatedProducts[index].description = `${packageType} for ${duration}`;
      updatedProducts[index].rate = rate;
    }

    setInvoiceData(prev => ({
      ...prev,
      products: updatedProducts
    }));
  };

  const updateProduct = (index, field, value) => {
    if (field === 'packageType' || field === 'duration') {
      updatePackageDetails(index, field, value);
    } else {
      const updatedProducts = [...invoiceData.products];
      updatedProducts[index][field] = typeof value === 'string' ? value : Number(value);

      const product = updatedProducts[index];
      const itemTaxable = product.qty * product.rate - product.discount;
      const isSameState = invoiceData.placeOfSupply.toLowerCase() === invoiceData.supplier.state.toLowerCase();

      if (isSameState) {
        product.cgst = parseFloat((itemTaxable * 0.09).toFixed(2));
        product.sgst = parseFloat((itemTaxable * 0.09).toFixed(2));
        product.igst = 0;
      } else {
        product.cgst = 0;
        product.sgst = 0;
        product.igst = parseFloat((itemTaxable * 0.18).toFixed(2));
      }

      setInvoiceData(prev => ({
        ...prev,
        products: updatedProducts
      }));
    }
  };

  return (
    <div className="invoice-page">
      <div className="top-action-bar">
    <button className="btn" onClick={onBack}>
      Back to List
    </button>

     <InvoiceControls
      isEditable={isEditable}
      setIsEditable={setIsEditable}
      saveInvoiceToDB={saveInvoiceToDB}
      downloadPDF={downloadPDF}
      addProduct={() => {
        setInvoiceData(prev => ({
          ...prev,
          products: [...prev.products, getDefaultProduct()]
        }));
      }}
      removeProduct={() => {
        setInvoiceData(prev => ({
          ...prev,
          products: prev.products.slice(0, -1)
        }));
      }}
      products={invoiceData.products}
    />
  </div>

      <div className={`invoice-container invoice-create-container ${!isEditable ? 'readonly-mode' : ''}`} ref={invoiceRef}>
        <InvoiceHeader supplier={invoiceData.supplier} />

        <div className="invoice-type-select no-print">
          <label className="font-medium">Invoice Type:</label>
          <select value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}>
            <option value="TAX INVOICE">TAX INVOICE</option>
            <option value="PROFORMA INVOICE">PROFORMA INVOICE</option>
          </select>
        </div>

        <h2 className="center">{invoiceType}</h2>

        <InvoiceMeta
          inv_number={invoiceData.inv_number}
          onInvNumberChange={handleInvNumberChange}
          invoiceNo={invoiceData.invoiceNo}
          invoiceDate={invoiceData.invoiceDate}
          placeOfSupply={invoiceData.placeOfSupply}
          isEditable={isEditable}
          setInvoiceData={setInvoiceData}
        />
        {lastInvoiceNumber && (
          <p className="last-invoice-number-display">
            Last Invoice Number: {lastInvoiceNumber}
          </p>
        )}

        <RecipientSection
          recipient={invoiceData.recipient}
          isEditable={isEditable}
          setInvoiceData={setInvoiceData}
        />

        <InvoiceTable
          products={invoiceData.products}
          packageOptions={packageOptions}
          isEditable={isEditable}
          updateProduct={updateProduct}
          formatCurrency={formatCurrency}
        />

        <SummaryBankRow
          bank={invoiceData.bank}
          totals={totals}
          amountInWords={amountInWords}
          formatCurrency={(amount) => `₹${amount.toFixed(2)}`}
          paidAmount={paidAmount}
          onPaidAmountChange={setPaidAmount}
          isEditable={isEditable}
          showPaymentFields={invoiceType !== "TAX INVOICE"}
        />

        {/* <TermsConditions /> */}
        <TermsConditions 
  value={termsConditions} 
  onChange={(e) => setTermsConditions(e.target.value)} 
  isEditable={isEditable}
/>

        <InvoiceFooter supplier={invoiceData.supplier} />
      </div>
    </div>
  );
};

export default InvoiceCreate;